'use client';

import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import { colors } from '@/styles/theme';
import { useTranslation } from '@/hooks/useTranslation';
import { interpolate } from '../_lib/i18n';
import { currencyApi, purchaseOrderApi, skuApi, supplierApi, type CurrencyItem, type RobotSku, type Supplier } from '../_lib/api';

interface LineDraft {
  key: string;
  skuId: string;
  quantity: number;
  unitPrice: number;
}

const newLine = (): LineDraft => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  skuId: '',
  quantity: 5,
  unitPrice: 22000,
});

/**
 * 新建 PO 对话框（支持多 line）
 *
 * 提交触发 backend 批量创建占位 RobotUnit（v3 业务设计）。
 * line 顺序决定 lineNo（1..N），occupy 序列全 PO 递增（NNN）。
 */
export function NewPoDialog({ onCreated }: { onCreated?: () => void }) {
  const { t } = useTranslation();
  const rm = t.robotManager;
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [poNo, setPoNo] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [currency, setCurrency] = useState<string>('USD');
  const [orderedAt, setOrderedAt] = useState<string>(''); // 采购日期（可选，空=用今天）
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);

  // dropdown options
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [skus, setSkus] = useState<RobotSku[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyItem[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingOptions(true);
    Promise.all([supplierApi.list(), skuApi.list(), currencyApi.list({ enabledOnly: true })])
      .then(([sups, sk, curs]) => {
        const supList: Supplier[] = Array.isArray(sups) ? sups : ((sups as any).items ?? []);
        const skuList: RobotSku[] = Array.isArray(sk) ? sk : ((sk as any).items ?? []);
        setSuppliers(supList);
        setSkus(skuList);
        setCurrencies(curs);
        if (curs.length > 0 && !curs.some((c) => c.code === currency)) {
          setCurrency(curs[0].code);
        }
        if (!poNo) setPoNo(`PO-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Date.now().toString().slice(-4)}`);
      })
      .catch((e) => toast.error(interpolate(rm.messages.dropdownLoadFailed, { msg: e?.message ?? '' })))
      .finally(() => setLoadingOptions(false));
  }, [open, poNo, currency]);

  const reset = () => {
    setPoNo('');
    setSupplierId('');
    setCurrency('USD');
    setOrderedAt('');
    setLines([newLine()]);
  };

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((arr) => arr.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const removeLine = (key: string) => {
    setLines((arr) => (arr.length <= 1 ? arr : arr.filter((l) => l.key !== key)));
  };
  const addLine = () => setLines((arr) => [...arr, newLine()]);

  const totalQty = lines.reduce((s, l) => s + (Number.isFinite(l.quantity) ? l.quantity : 0), 0);
  const totalAmount = lines.reduce((s, l) => s + (l.quantity || 0) * (l.unitPrice || 0), 0);

  const handleSubmit = async () => {
    if (!poNo || !supplierId) {
      toast.error(rm.messages.fillPoSupplier);
      return;
    }
    if (lines.length === 0) {
      toast.error(rm.messages.oneLineRequired);
      return;
    }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.skuId || l.quantity <= 0 || l.unitPrice <= 0) {
        toast.error(interpolate(rm.messages.lineSkuQtyPriceRequired, { n: i + 1 }));
        return;
      }
    }
    setSubmitting(true);
    try {
      await purchaseOrderApi.create({
        poNo,
        supplierId,
        currencyCode: currency,
        // 采购日期可选：用户填了用用户的，没填用今天
        orderedAt: orderedAt ? new Date(orderedAt).toISOString() : new Date().toISOString(),
        lines: lines.map((l, idx) => ({
          lineNo: idx + 1,
          skuId: l.skuId,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          currencyCode: currency,
          placeholderPattern: '{poNo}-LINE-{NNN}',
        })),
      });
      toast.success(interpolate(rm.messages.poCreatedSummary, { poNo, lines: lines.length, total: totalQty }));
      setOpen(false);
      reset();
      onCreated?.();
    } catch (e: any) {
      toast.error(interpolate(rm.messages.createFailedReason, { msg: e?.message ?? '' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-1" />
          {rm.form.newPoButton}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{rm.form.newPoTitle}</DialogTitle>
          <DialogDescription>
            {rm.form.newPoDesc}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-4 gap-3">
            <Field label={rm.form.poNoLabel}>
              <Input
                value={poNo}
                onChange={(e) => setPoNo(e.target.value)}
                placeholder={rm.placeholders.poNoAutoOrManual}
                disabled={submitting}
                className="h-9 text-sm"
              />
            </Field>
            <Field label={rm.form.orderedAtOptional}>
              <Input
                type="date"
                value={orderedAt}
                onChange={(e) => setOrderedAt(e.target.value)}
                disabled={submitting}
                className="h-9 text-sm"
                placeholder={rm.placeholders.orderedAtEmpty}
              />
            </Field>
            <Field label={rm.form.supplier}>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                disabled={submitting || loadingOptions}
                className="w-full h-9 px-2 text-sm border rounded bg-white"
                style={{ borderColor: colors.border }}
              >
                <option value="">{loadingOptions ? rm.form.loadingOption : rm.form.selectSupplierOption}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} · {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={rm.form.currency}>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={submitting || loadingOptions}
                className="w-full h-9 px-2 text-sm border rounded bg-white"
                style={{ borderColor: colors.border }}
              >
                {loadingOptions ? (
                  <option value="">{rm.form.loadingOption}</option>
                ) : currencies.length === 0 ? (
                  <option value="">{rm.form.noCurrencies}</option>
                ) : (
                  currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}{c.symbol ? ` ${c.symbol}` : ''} · {c.name}
                    </option>
                  ))
                )}
              </select>
            </Field>
          </div>

          <div className="pt-3 border-t" style={{ borderColor: colors.border }}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-sm font-medium" style={{ color: colors.textPrimary }}>
                {rm.form.poLines}
              </div>
              <div className="text-xs" style={{ color: colors.textSecondary }}>
                {interpolate(rm.form.poLinesSummary, { lines: lines.length, qty: totalQty, currency, total: totalAmount.toLocaleString() })}
              </div>
            </div>

            <div
              className="rounded border overflow-hidden"
              style={{ borderColor: colors.border }}
            >
              {/* 列头 */}
              <div
                className="grid grid-cols-[2rem_1fr_5rem_7rem_2rem] gap-2 px-3 py-2 text-xs font-medium"
                style={{
                  color: colors.textSecondary,
                  backgroundColor: colors.bgSecondary,
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <span>#</span>
                <span>SKU</span>
                <span className="text-right">{rm.form.quantity}</span>
                <span className="text-right">{interpolate(rm.form.unitPriceWithCurrency, { currency })}</span>
                <span></span>
              </div>

              {/* 数据行 */}
              {lines.map((line, idx) => (
                <div
                  key={line.key}
                  className="grid grid-cols-[2rem_1fr_5rem_7rem_2rem] gap-2 px-3 py-1.5 items-center"
                  style={{
                    borderBottom: idx < lines.length - 1 ? `1px solid ${colors.border}` : undefined,
                  }}
                >
                  <span className="text-xs font-mono" style={{ color: colors.textTertiary }}>
                    {idx + 1}
                  </span>
                  <select
                    value={line.skuId}
                    onChange={(e) => updateLine(line.key, { skuId: e.target.value })}
                    disabled={submitting || loadingOptions}
                    className="w-full px-2 py-1 text-sm border rounded bg-white"
                    style={{ borderColor: colors.border }}
                  >
                    <option value="">{loadingOptions ? rm.form.loadingOption : rm.form.selectSku}</option>
                    {skus.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.code} · {s.name}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateLine(line.key, { quantity: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                    disabled={submitting}
                    className="h-7 text-sm text-right"
                  />
                  <Input
                    type="number"
                    min={0}
                    value={line.unitPrice}
                    onChange={(e) => updateLine(line.key, { unitPrice: Math.max(0, parseFloat(e.target.value || '0')) })}
                    disabled={submitting}
                    className="h-7 text-sm text-right"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(line.key)}
                    disabled={submitting || lines.length <= 1}
                    title={lines.length <= 1 ? rm.form.atLeastKeepOneLine : rm.form.deleteThisLine}
                    className="h-7 w-7 p-0"
                  >
                    <Trash2
                      className="w-3.5 h-3.5"
                      style={{ color: lines.length <= 1 ? colors.textTertiary : colors.textSecondary }}
                    />
                  </Button>
                </div>
              ))}

              {/* + 添加行（行尾按钮） */}
              <button
                type="button"
                onClick={addLine}
                disabled={submitting}
                className="w-full px-3 py-2 text-xs flex items-center justify-center gap-1 transition-colors"
                style={{
                  color: colors.primary,
                  backgroundColor: colors.bgSecondary,
                  borderTop: `1px solid ${colors.border}`,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                <Plus className="w-3 h-3" />
                {rm.actions.addLine}
              </button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            {rm.actions.cancel}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                {rm.actions.creating}
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                {interpolate(rm.form.createPoButton, { total: totalQty })}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium mb-1 block" style={{ color: colors.textPrimary }}>
        {label}
      </span>
      {children}
    </label>
  );
}
